"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Check, Clock, Sparkles, Calendar as CalendarIcon, Send, ShieldCheck, Heart } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
interface PricingPlan {
  duration: string;
  title: string;
  price: string;
  currency: string;
  description: string;
  popular?: boolean;
  features: string[];
}
const pricingPlans: PricingPlan[] = [{
  duration: "30 Min.",
  title: "Kennenlerngespräch",
  price: "30",
  currency: "CHF",
  description: "Fokussierte Kurz-Session zur akuten Orientierung oder für ein sanftes Kennenlernen.",
  features: ["Gezielte Standortbestimmung", "Sanfte energetische Zentrierung", "Konkreter Impuls für den Alltag", "Online oder vor Ort möglich"]
}, {
  duration: "90 MIN.",
  title: "Reiki Behandlung, Ganzheitliche Begleitung",
  price: "180",
  currency: "CHF",
  description: "Tiefgehende Einzelsitzung zur emotionalen Klärung und nachhaltigen Trauma-Entlastung.",
  popular: true,
  features: ["Ausführliche Bedürfnisanalyse", "Kombination aus Energiearbeit & Gespräch", "Nervensystem-Regulation & Erdung", "Individuelle Integrationsübung für zu Hause"]
}, {
  duration: "90 MIN. \xA0",
  title: "Energetische Heilung Intensiv Paket",
  price: "850",
  currency: "CHF",
  description: "Umfassender Raum für tief sitzende Blockaden, Traumaheilung und nachhaltigen Wandel.",
  features: ["Erweiterte geführte Prozessarbeit", "Tiefen-Meditation & Resonanz-Heilung", "Ganzheitliche Ressourcenstärkung", "Nachbetreuung 1 Telefonisches Gespräch\xA0"]
}];
function AppointmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    sessionType: "60 Min. - Ganzheitliche Begleitung",
    preferredDate: "",
    preferredTime: "",
    message: ""
  });
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card border border-border rounded-xl shadow-sm text-center flex flex-col items-center justify-center min-h-[420px]">
        <div className="w-12 h-12 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center mb-4">
          <Heart className="w-6 h-6" />
        </div>
        <h3 className="text-xl font-serif font-semibold text-foreground mb-2">
          Herzlichen Dank für Ihre Anfrage
        </h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6">
          {successMessage || "Ihre Terminanfrage ist eingegangen. Wir melden uns innerhalb von 24 Stunden mit Terminvorschlägen bei Ihnen."}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md">
          Weitere Anfrage senden
        </Button>
      </div>;
  }
  return <form onSubmit={e => {
    e.preventDefault();
    handleSubmit();
  }} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-foreground">
            Name & Vorname *
          </Label>
          <Input id="name" required value={formData.name} onChange={e => handleChange("name", e.target.value)} placeholder="z.B. Elena Meier" className="rounded-md bg-card border-border text-foreground px-3 py-2" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-foreground">
            E-Mail-Adresse *
          </Label>
          <Input id="email" type="email" required value={formData.email} onChange={e => handleChange("email", e.target.value)} placeholder="elena.meier@beispiel.ch" className="rounded-md bg-card border-border text-foreground px-3 py-2" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="phone" className="text-foreground">
            Telefonnummer (optional)
          </Label>
          <Input id="phone" type="tel" value={formData.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="+41 79 000 00 00" className="rounded-md bg-card border-border text-foreground px-3 py-2" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sessionType" className="text-foreground">
            Gewünschtes Format
          </Label>
          <select id="sessionType" value={formData.sessionType} onChange={e => handleChange("sessionType", e.target.value)} className="w-full h-9 rounded-md border border-border bg-card px-3 text-sm text-foreground focus-visible:border-ring focus-visible:ring-ring/50 outline-none">
            <option value="30 Min. - Klarheits-Impuls">30 Min. – Klarheits-Impuls (85 CHF)</option>
            <option value="60 Min. - Ganzheitliche Begleitung">60 Min. – Ganzheitliche Begleitung (160 CHF)</option>
            <option value="90 Min. - Intensiv-Transformation">90 Min. – Intensiv-Transformation (230 CHF)</option>
            <option value="Retreat Beratung">Retreat-Vorabgespräch (kostenfrei)</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="preferredDate" className="text-foreground">
            Wunschdatum
          </Label>
          <Input id="preferredDate" type="date" value={formData.preferredDate} onChange={e => handleChange("preferredDate", e.target.value)} className="rounded-md bg-card border-border text-foreground px-3 py-2" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="preferredTime" className="text-foreground">
            Bevorzugte Tageszeit
          </Label>
          <select id="preferredTime" value={formData.preferredTime} onChange={e => handleChange("preferredTime", e.target.value)} className="w-full h-9 rounded-md border border-border bg-card px-3 text-sm text-foreground focus-visible:border-ring focus-visible:ring-ring/50 outline-none">
            <option value="flexibel">Flexibel</option>
            <option value="vormittags">Vormittags (09:00 - 12:00)</option>
            <option value="nachmittags">Nachmittags (13:00 - 17:00)</option>
            <option value="abends">Abends (ab 17:30)</option>
          </select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="message" className="text-foreground">
          Ihr Anliegen oder Ihre Fragen
        </Label>
        <Textarea id="message" rows={4} value={formData.message} onChange={e => handleChange("message", e.target.value)} placeholder="Beschreiben Sie kurz, welches Thema Sie mitbringen möchten oder welche Fragen Sie haben..." className="rounded-md bg-card border-border text-foreground px-3 py-2" />
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 text-base font-medium rounded-md shadow-sm">
          {isSubmitting ? <span className="flex items-center gap-2">
              <span className="animate-spin inline-block size-4 border-2 border-current border-t-transparent rounded-full" />
              Wird gesendet...
            </span> : <span className="flex items-center justify-center gap-2">
              <Send className="w-4 h-4" />
              Termin unverbindlich anfragen
            </span>}
        </Button>
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-1">
        <ShieldCheck className="w-4 h-4 text-primary" />
        <span>Diskret & vertraulich – Verbindliche Buchung erst nach gegenseitiger Bestätigung</span>
      </div>
    </form>;
}
export default function Pricing() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="pricingandappointment" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-1/4 -left-48 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-10 right-0 w-80 h-80 bg-secondary/30 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 bg-secondary text-secondary-foreground rounded-md text-xs tracking-wide">Investition in Dein Wohlbefinden</Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight mb-4">
            Preise & Terminvereinbarung
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Transparente Konditionen für Einzelsitzungen und persönliche Begleitung. Wähle die passende Dauer für Dein Anliegen und <Link to="/ContactPage" className="underline cursor-pointer">vereinbare direkt Deinen Wunschtermin</Link>.<br />Ich ersetze  keinen Arzt</p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="lg:col-span-6 space-y-5">
            <div className="flex items-center justify-between pb-2 border-b border-border">
              <h3 className="text-xl font-serif font-semibold text-foreground">
                Sitzungsformate & Tarife
              </h3>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clock w-3.5 h-3.5" aria-hidden="true"><path d="M12 6v6l4 2"></path><circle cx="12" cy="12" r="10"></circle></svg>Vor Ort&nbsp;</span>
            </div>

            {pricingPlans.map((plan, i) => <motion.div key={plan.title} variants={itemVariants} data-index={i}>
                <Card className={`relative border transition-all rounded-xl ${plan.popular ? "border-primary/40 bg-card shadow-md ring-1 ring-primary/20" : "border-border bg-card shadow-xs hover:border-border/80"}`}>
                  {plan.popular && <div className="absolute -top-3 right-6">
                      
                    </div>}

                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                            {plan.duration}
                          </span>
                        </div>
                        <CardTitle className="text-lg font-serif font-bold text-card-foreground">
                          {plan.title}
                        </CardTitle>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="flex items-baseline justify-end gap-1">
                          <span className="text-2xl font-bold font-serif text-foreground">
                            {plan.price}
                          </span>
                          <span className="text-xs font-medium text-muted-foreground">
                            {plan.currency}
                          </span>
                        </div>
                        <span className="text-[11px] text-muted-foreground">{i === 2 ? "5 Einzelsitzungen" : "pro Einzelsitzung"}</span>
                      </div>
                    </div>
                    <CardDescription className="text-xs text-muted-foreground mt-1">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 pb-4">
                    <ul className="space-y-2 text-xs text-foreground/85 border-t border-border/60 pt-3">
                      {plan.features.map((feat, featIdx) => <li key={featIdx} className="flex items-center gap-2">
                          <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                          <span>{feat}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>)}

            <div className="p-4 bg-muted rounded-xl border border-border/70 text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">Hinweis zur Abrechnung:</p>
              <p>Termine können bis 24 Stunden im Voraus kostenfrei verschoben oder storniert werden. Bezahlung bequem per TWINT oder bar vor Ort.</p>
            </div>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-6">
            <Card className="border border-border bg-card shadow-md rounded-xl p-6 md:p-8">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider mb-1">
                  <SafeHydrate><CalendarIcon className="w-4 h-4" /></SafeHydrate>
                  <span>Unverbindliche Terminanfrage</span>
                </div>
                <h3 className="text-2xl font-serif font-bold text-foreground">
                  Termin buchen
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Teilen Sie uns Ihre Präferenzen mit. Wir kontaktieren Sie schnellstmöglich zur festen Bestätigung.
                </p>
              </div>

              <FormProvider formId="appointment-booking-pricing" sectionName="pricing-and-appointment" defaultValues={{
              name: "",
              email: "",
              phone: "",
              sessionType: "60 Min. - Ganzheitliche Begleitung",
              preferredDate: "",
              preferredTime: "flexibel",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              }
            }} submitText="Termin buchen" successMessage="Vielen Dank! Ihre Terminanfrage wurde erfolgreich übermittelt. Wir melden uns in Kürze mit passenden Zeitfenstern." errorMessage="Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt per Mail.">
                <AppointmentForm />
              </FormProvider>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}